import React, { useEffect, useState } from 'react';
import { styled as styled2 } from '@compiled/react';
import FeedbackIcon from '@atlaskit/icon/glyph/feedback';
import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useGetCustomFields } from '@atlassian/jira-servicedesk-custom-fields/src/services/use-get-custom-fields/index.tsx';
import { SettingsCardComponent } from '@atlassian/jira-servicedesk-settings-common/src/ui/settings-card-component';
import { RESPONDERS_FIELD_KEY, MAJOR_INCIDENT_FIELD_KEY } from '../../common/constants';
import {
	settingsCardComponentStyles,
	SectionHeading,
	sectionParagraphStyles,
} from '../../common/ui';
import { ChatChannelField } from './chat-channel-field';
import { ExpandButton } from './expand-button';
import { LinkedAlertsField } from './linked-alerts-field';
import { MajorIncidentField } from './major-incident-field';
import messages from './messages';
import { RespondersField } from './responders-field';
import { StakeholdersField } from './stakeholders-field';

const IncidentManagementSectionTitle = ({
	isExpanded,
	setIsExpanded,
}: {
	isExpanded: boolean;
	setIsExpanded: (newValue: boolean) => void;
}) => {
	const { formatMessage } = useIntl();
	return (
		<ExpandableSectionTitleContainer isExpanded={isExpanded}>
			<LeftSideTitleContainer>
				<FeedbackIcon label="" />
				<SectionHeading>{formatMessage(messages.incidentManagementSectionHeading)}</SectionHeading>
			</LeftSideTitleContainer>
			<ExpandButton
				label={formatMessage(messages.newIncidentManagementExpandButtonLabel)}
				onClick={() => setIsExpanded(!isExpanded)}
				isExpanded={isExpanded}
			/>
		</ExpandableSectionTitleContainer>
	);
};

export const IncidentManagementFieldsSection = () => {
	const [isIncidentManagementExpanded, setIsIncidentManagementExpanded] = useState(true);
	const [doesRespondersFieldExist, setDoesRespondersFieldExist] = useState(true);
	const [doesMajorIncidentFieldExist, setDoesMajorIncidentFieldExist] = useState(true);

	const [{ data: customFields, error: customFieldsError }] = useGetCustomFields();
	const [respondersWarningFlagVisible, setRespondersWarningFlagVisible] = useState<boolean>(false);
	const [majorIncidentWarningFlagVisible, setMajorIncidentWarningFlagVisible] =
		useState<boolean>(false);

	useEffect(() => {
		if (customFields) {
			if (customFields[RESPONDERS_FIELD_KEY] == null) {
				setDoesRespondersFieldExist(false);
			}

			if (customFields[MAJOR_INCIDENT_FIELD_KEY] == null) {
				setDoesMajorIncidentFieldExist(false);
			}
		}
	}, [customFields]);

	return (
		<SettingsCardComponent
			title={
				<IncidentManagementSectionTitle
					isExpanded={isIncidentManagementExpanded}
					setIsExpanded={setIsIncidentManagementExpanded}
				/>
			}
			styles={settingsCardComponentStyles}
		>
			{isIncidentManagementExpanded ? (
				<>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
					<Box xcss={[sectionParagraphStyles]} />
					<RespondersField
						isFieldMissing={!doesRespondersFieldExist && customFieldsError === null}
						isFlagVisible={respondersWarningFlagVisible}
						setFlagVisible={setRespondersWarningFlagVisible}
					/>
					<ChatChannelField />
					<StakeholdersField />
					<LinkedAlertsField />
					<MajorIncidentField
						isFieldMissing={!doesMajorIncidentFieldExist && customFieldsError === null}
						isFlagVisible={majorIncidentWarningFlagVisible}
						setFlagVisible={setMajorIncidentWarningFlagVisible}
					/>
				</>
			) : null}
		</SettingsCardComponent>
	);
};

type ExpandableSectionTitleContainerProps = {
	isExpanded?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandableSectionTitleContainer = styled2.div<ExpandableSectionTitleContainerProps>(
	{
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isExpanded }: ExpandableSectionTitleContainerProps) =>
		isExpanded ? `margin-bottom: ${token('space.400', '32px')};` : 'margin-bottom: 0',
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LeftSideTitleContainer = styled2.div({
	display: 'flex',
	alignItems: 'center',
});
